import { memo, useCallback, useMemo } from 'react';

import { CommentsRow } from '~/components/ShareModal/components/InfoCommentsSection/CommentsRow';
import { useShowcaseModal } from '~/components/ShowcaseModal/ShowcaseModalContext';
import { type PermissionMode } from '~/components/ShowcaseModal/types';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { canEditAssetDiscussions, canViewAssetDiscussions } from '~/utils/permissions/assetPermissions';

/** Create-mode smart leaf: comments permission segmented row. Writes draft state, no per-change analytics. */
export const CreateShowcaseSettingsCommentsRow = memo(() => {
  const { shortUrlProps, isSaving, updateCommentPermissions } = useShowcaseModal();
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();

  const canManageDiscussions = useMemo(() => canEditAssetDiscussions(workspacePermissions), [workspacePermissions]);
  const canViewDiscussions = useMemo(() => canViewAssetDiscussions(workspacePermissions), [workspacePermissions]);

  const handleCommentsChange = useCallback(
    ({ canView, canManage }: { canView: boolean; canManage: boolean }) => {
      let mode: PermissionMode;

      if (canView && canManage) {
        mode = 'allow';
      } else if (canView) {
        mode = 'read-only';
      } else {
        mode = 'hide';
      }

      updateCommentPermissions(mode);
    },
    [updateCommentPermissions],
  );

  return (
    <CommentsRow
      canManage={shortUrlProps.permissions.asset.canManageDiscussions}
      canManageDiscussions={canManageDiscussions}
      canView={shortUrlProps.permissions.asset.canViewDiscussions}
      canViewDiscussions={canViewDiscussions}
      data-testid="COMMENTS_SEGMENTED_CONTROL"
      isLoading={isSaving}
      onChange={handleCommentsChange}
    />
  );
});

CreateShowcaseSettingsCommentsRow.displayName = 'CreateShowcaseSettingsCommentsRow';
